/**
 * The research wait. Same contract as `ReportWaitStep`: a candid message
 * with no promised duration, the three capability cards, a demo request
 * kept secondary, and the ready state replacing any open preview.
 */

import React, { useState } from 'react';
import MockPanel from './MockPanel';
import CapabilityPreview from '../CapabilityPreview';
import { copyFor, SHARED } from '../audienceCopy';

// `onBackToList`, not `onBack`: the other design has always called it that
// and so does the call site. Under the old name the prop arrived undefined
// and the button did nothing at all — a dead control that looks live, which
// is exactly what a prop-name typo buys you in a language with no contract
// between the two sides.
export default function MockReportWait({
  audience, storyTitle, status, onOpenReport, onBackToList, onRetry, onRequestDemo,
}) {
  const copy = copyFor(audience);
  const [openPanel, setOpenPanel] = useState(null);
  const ready = status === 'ready';
  const failed = status === 'failed';
  // A settled job closes any open preview — the result outranks a preview.
  const previewKey = ready || failed ? null : openPanel;

  const panels = Object.entries(copy.capabilities || {});

  return (
    <>
      <div className="mk-actions" style={{ marginTop: 0, marginBottom: 20 }}>
        <button type="button" className="mk-btn mk-btn--ghost" onClick={onBackToList}>
          ← {SHARED.backToOpportunities}
        </button>
      </div>

      <p className="mk-eyebrow">{ready || failed ? 'Research' : 'Preparing research'}</p>
      {/* Three states, three headings. This used to show `reportReadyTitle`
          whenever the run had not failed, so a job still in flight was
          headed "Your research is ready" with the spinner turning beside it
          and the eyebrow underneath reading "Preparing research" — the
          screen contradicting itself, and a claim about state that §2
          forbids. The other design has always branched on `ready`. */}
      <h1 className="mk-h1 mk-h1--sm">
        {ready ? copy.reportReadyTitle
          : failed ? 'We could not prepare this research.'
            : copy.reportWait}
      </h1>
      {storyTitle && <p className="mk-lede">{storyTitle}</p>}

      {/* `--pinned`, because this column EXPANDS: opening a preview adds
          ~500px to it, and a centred panel slid down by half of that every
          time — the one fixed thing on the screen moving because something
          else grew. The panel holds the top of the row and sticks there. */}
      <div className="mk-split mk-split--pinned">
        <div>
          {failed ? (
            <>
              <div className="mk-notice">{SHARED.reportFailed}</div>
              <div className="mk-actions">
                <button type="button" className="mk-btn mk-btn--primary" onClick={onRetry}>
                  {SHARED.retry}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mk-eyebrow">The wider platform</p>
              <h2 className="mk-h2">Go further when you are ready</h2>
              {panels.map(([key, panel]) => (
                <div className="mk-card" key={key}>
                  <h3 className="mk-h3">{panel.title}</h3>
                  <p className="mk-body">{panel.body}</p>
                  <button
                    type="button"
                    className="mk-link"
                    style={{ marginTop: 10 }}
                    onClick={() => setOpenPanel(previewKey === key ? null : key)}
                  >
                    {previewKey === key ? 'Hide preview' : `${SHARED.seePreview} →`}
                  </button>
                  <CapabilityPreview panelKey={key} title={panel.title} skin="mk" revealed={previewKey === key} />
                </div>
              ))}
              {onRequestDemo && (
                <div className="mk-actions">
                  <button type="button" className="mk-btn mk-btn--ghost" onClick={onRequestDemo}>
                    {SHARED.requestAGuidedDemo}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <MockPanel
          busy={!ready && !failed}
          working={SHARED.panelPreparingReport}
          title={ready ? null : failed ? 'Stopped before finishing' : null}
          sub={ready ? 'Open it whenever you are ready.' : null}
          action={ready ? (
            <button type="button" className="mk-btn mk-btn--primary" onClick={onOpenReport}>
              {SHARED.openResearch}
            </button>
          ) : null}
        />
      </div>
    </>
  );
}
